import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./Header.css";
import logo from "../../imges/logos/mindsinaction/logo.png";
import { useAuth } from "../../auth/AuthContext";
import { Box, ButtonBase, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CelebrationIcon from '@mui/icons-material/Celebration';
import CloseIcon from '@mui/icons-material/Close';
// You need to import Container and Toolbar from MUI
import { Container, Toolbar } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const { isAuthenticated, logout, getData } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [specialAnnouncements, setSpecialAnnouncements] = useState([]);
  const [display, setDisplay] = useState("none");
  const [showHolidayBanner, setShowHolidayBanner] = useState(false);
  const [holidayMessage, setHolidayMessage] = useState("");
  const [scrollingText, setScrollingText] = useState("");
  const marqueeRef = useRef(null);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Holiday configuration
  const holidays = [
    {
      name: "New Year",
      startDate: "01-01",
      endDate: "01-20",
      message: "🎉 Happy New Year! Wishing you prosperity and success in the coming year! 🎉",
      icon: "🎉"
    },
    {
      name: "Christmas",
      date: "12-25",
      message: "🎄 Merry Christmas! May your holidays be filled with joy and peace! 🎄",
      icon: "🎄"
    }
  ];

  useEffect(() => {
    const fetchBackgrounds = async () => {
      try {
        const moduleResponse = await getData("proccess.php", "our_clients_V2");
        if (moduleResponse.data && Array.isArray(moduleResponse.data)) {
          setModules(moduleResponse.data);
        } else {
          console.error("Expected an array for modules, but got:", moduleResponse);
          setModules([]);
        }

        const response = await getData("proccess.php", "department");
        if (response.success) {
          setServices(response.data);
        }

        const specialAnnouncementsResponse = await getData("proccess.php", "special_announcements");
        if (specialAnnouncementsResponse.success) {
          setSpecialAnnouncements(specialAnnouncementsResponse.data);
        }

        const publicHolidaysResponse = await getData("proccess.php", "public_holidays");
        if (publicHolidaysResponse.success) {
          setPublicHolidays(publicHolidaysResponse.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setModules([]);
      }
    };
    fetchBackgrounds();
  }, [getData]);

  // Check for holidays and prepare scrolling text
  useEffect(() => {
    if (!publicHolidays.length && !specialAnnouncements.length) return;

    const checkHolidays = () => {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentDate = today.getDate();

      const todayFormatted = `${String(currentMonth).padStart(2, "0")}-${String(
        currentDate
      ).padStart(2, "0")}`;

      let messages = [];

      // 🎉 New Year period
      if (currentMonth === 1 && currentDate >= 1 && currentDate <= 20) {
        messages.push(
          "🎉 Happy New Year! Wishing you prosperity and success in the coming year! 🎉"
        );
      }

      // 🎄 Christmas
      if (todayFormatted === "12-25") {
        messages.push(
          "🎄 Merry Christmas! May your holidays be filled with joy and peace! 🎄"
        );
      }

      // 🇳🇦 Public holidays from database
      const holiday = publicHolidays.find(
        (h) => h.holiday_date === todayFormatted
      );

      if (holiday) {
        messages.push(holiday.message);
      }

      // 📢 Special announcements (ONLY ACTIVE)
      if (messages.length === 0 && specialAnnouncements.length > 0) {

        const activeAnnouncements = specialAnnouncements.filter(
          (a) => Number(a.is_active) === 1
        );

        if (activeAnnouncements.length > 0) {
          const dayOfYear = Math.floor(
            (today - new Date(today.getFullYear(), 0, 0)) /
              (1000 * 60 * 60 * 24)
          );

          const announcementIndex = dayOfYear % activeAnnouncements.length;

          messages.push(
            activeAnnouncements[announcementIndex].announcement
          );
        }
      }

      if (messages.length > 0) {
        const separator = "   •••   ";
        const repeatedMessage = (messages[0] + separator).repeat(8);

        setScrollingText(repeatedMessage);
        setHolidayMessage(messages[0]);
        setShowHolidayBanner(true);
      } else {
        setShowHolidayBanner(false);
      }
    };

    checkHolidays();

    const intervalId = setInterval(checkHolidays, 60000);

    return () => clearInterval(intervalId);
  }, [publicHolidays, specialAnnouncements]);

  // Marquee animation effect
  useEffect(() => {
    if (!marqueeRef.current || !showHolidayBanner) return;

    const marqueeElement = marqueeRef.current;
    const textWidth = marqueeElement.scrollWidth;
    
    // Calculate animation duration based on text length
    const duration = Math.max(30, textWidth / 25); // Minimum 30 seconds
    
    // Set up animation
    marqueeElement.style.animation = `marquee ${duration}s linear infinite`;
    
    // Reset animation periodically to prevent memory issues
    const resetAnimation = () => {
      marqueeElement.style.animation = 'none';
      void marqueeElement.offsetWidth; // Trigger reflow
      marqueeElement.style.animation = `marquee ${duration}s linear infinite`;
    };
    
    const resetInterval = setInterval(resetAnimation, 300000); // Reset every 5 minutes
    
    return () => {
      clearInterval(resetInterval);
      if (marqueeElement) {
        marqueeElement.style.animation = '';
      }
    };
  }, [scrollingText, showHolidayBanner]);

  const handleMouseEnter = (dropdownName) => {
    setActiveDropdown(dropdownName);
  };

  const handleDisplay = (display) => {
    display === "none" ? setDisplay("block") : setDisplay("none");
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileDropdown = (dropdownClass) => {
    const dropdown = document.querySelector(`.${dropdownClass}`);
    if (dropdown) {
      dropdown.classList.toggle('show');
    }
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate("/");
  };

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setDisplay("none");
  };

  const getNavLinkClass = ({ isActive }) =>
    isActive ? "nav-link active" : "nav-link";

  const getMobileNavLinkClass = ({ isActive }) =>
    isActive ? "mobile-nav-link active" : "mobile-nav-link";

  return (
    <>
      {/* Infinite Scrolling Holiday Banner */}
      {showHolidayBanner && (
        <Box 
          className="holiday-banner-container"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            animation: 'slideDown 0.5s ease-out',
            overflow: 'hidden',
            backgroundColor: '#000000ff',
            background: 'linear-gradient(90deg, #000000ff 0%, #424242ff 50%, #000000ff 100%)',
            color: 'white',
            py: { xs: 0.5, sm: 0.75 },
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            borderBottom: '2px solid #FFD700'
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 }, overflow: 'hidden' }}>
            <Toolbar sx={{ 
              justifyContent: 'flex-start', 
              minHeight: { xs: '32px !important', sm: '36px !important' },
              padding: '0 !important',
              position: 'relative',
              width: '100%',
              overflow: 'hidden'
            }}>
              {/* Left Icon */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                left: 0,
                zIndex: 2,
                backgroundColor: '#000000ff',
                paddingRight: 1,
                paddingLeft: { xs: 0, sm: 1 },
                height: '100%'
              }}>
                <CelebrationIcon sx={{ 
                  color: '#FFD700',
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  animation: 'bounce 2s infinite',
                  filter: 'drop-shadow(0 0 2px rgba(255,215,0,0.5))'
                }} />
              </Box>
              
              {/* Marquee Container */}
              <Box className="marquee-container" sx={{
                flex: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                position: 'relative',
                paddingLeft: { xs: '28px', sm: '32px' },
                paddingRight: { xs: '28px', sm: '32px' },
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
                {/* Marquee Text */}
                <Box
                  ref={marqueeRef}
                  className="marquee-text"
                  sx={{
                    display: 'inline-block',
                    paddingLeft: '100%', // Start off-screen
                    whiteSpace: 'nowrap',
                    willChange: 'transform',
                    animation: 'marquee 30s linear infinite',
                    '&:hover': {
                      animationPlayState: 'paused'
                    }
                  }}
                >
                  <Typography 
                    component="span"
                    variant="body1" 
                    className="scrolling-text"
                    sx={{ 
                      display: 'inline-block',
                      fontWeight: '600',
                      fontSize: { xs: '0.9rem', sm: '0.9rem', md: '1rem' },
                      letterSpacing: '0.3px',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      paddingRight: '60px', // Space between repetitions
                      color: '#FFF',
                      textTransform: 'uppercase'
                    }}
                  >
                    {scrollingText}
                  </Typography>
                </Box>
                
                {/* Gradient overlays for smooth edges */}
                <Box className="marquee-overlay-left" sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '40px',
                  background: 'linear-gradient(to right, #000000ff, transparent)',
                  zIndex: 1,
                  pointerEvents: 'none'
                }} />
                <Box className="marquee-overlay-right" sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '40px',
                  background: 'linear-gradient(to left, #000000ff, transparent)',
                  zIndex: 1,
                  pointerEvents: 'none'
                }} />
              </Box>
              
              {/* Right Icon */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                right: 0,
                zIndex: 2,
                backgroundColor: '#000000ff',
                paddingLeft: 1,
                paddingRight: { xs: 0, sm: 1 },
                height: '100%'
              }}>
                <CelebrationIcon sx={{ 
                  color: '#FFD700',
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  animation: 'bounce 2s infinite 1s', // Delay for alternating bounce
                  filter: 'drop-shadow(0 0 2px rgba(255,215,0,0.5))'
                }} />
              </Box>
            </Toolbar>
          </Container>
        </Box>
      )}

      {/* Main Header */}
      <header className="header" style={{ 
        top: showHolidayBanner ? (window.innerWidth < 600 ? '50px' : '50px') : '0',
        transition: 'top 0.3s ease'
      }}>
        <div className="header-container">
          {/* Logo */}
          <div className="logo">
            <NavLink to="/" onClick={scrollToTop}>
              <img src={logo} alt="Company Logo" />
            </NavLink>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>

          {/* Main Navigation - Desktop */}
          <nav className="main-nav">
            <ul className="nav-list">
              <li className="nav-item">
                <NavLink to="/" onClick={scrollToTop} className={getNavLinkClass} end>
                  Home
                </NavLink>
              </li>

              {/* About Dropdown */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("about")}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  setActiveDropdown(activeDropdown === "about" ? null : "about");
                }}
              >
                <NavLink to="/about" className={getNavLinkClass}>
                  Who We Are
                </NavLink>
              </li>

              {/* Modules Dropdown */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("modules")}
                onMouseLeave={handleMouseLeave}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "modules" ? null : "modules"
                  )
                }
              >
                <NavLink to="/modules" className={getNavLinkClass}>
                  Modules <span className="dropdown-icon">▼</span>
                </NavLink>
                <ul
                  className={`dropdown-menu ${
                    activeDropdown === "modules" ? "show" : ""
                  }`}
                >
                  {modules.map((client) => (
                    <li key={client.id}>
                      <NavLink
                        to={`./modules/category/${client.categories}/?cat=${client.categories}`}
                        className={getNavLinkClass}
                        onClick={() => {
                          setActiveDropdown(null);
                        }}
                        style={{ textTransform: "capitalize" }}
                      >
                        {client.title}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Services Dropdown */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("services")}
                onMouseLeave={handleMouseLeave}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "services" ? null : "services"
                  )
                }
              >
                <NavLink to="/services" className={getNavLinkClass}>
                  Services <span className="dropdown-icon">▼</span>
                </NavLink>
                <ul
                  className={`dropdown-menu ${
                    activeDropdown === "services" ? "show" : ""
                  }`}
                >
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "STEAM" && (
                        <li key={service.id}>
                          <NavLink
                            to={`/services/${service.department_name}/?code=${service.id}`}
                            className={getNavLinkClass}
                            onClick={() => {
                              setActiveDropdown(null);
                            }}
                          >
                            {service.department_name}
                          </NavLink>
                        </li>
                      )
                    ))
                  }
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "ICT" && (
                        <li key={service.id}>
                          <NavLink
                            to={`/services/${service.department_name}/?code=${service.id}`}
                            className={getNavLinkClass}
                            onClick={() => {
                              setActiveDropdown(null);
                            }}
                          >
                            {service.department_name}
                          </NavLink>
                        </li>
                      )
                    ))
                  }
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "Engineering" && (
                        <li key={service.id}>
                          <NavLink
                            to={`/services/${service.department_name}/?code=${service.id}`}
                            className={getNavLinkClass}
                            onClick={() => {
                              setActiveDropdown(null);
                            }}
                          >
                            {service.department_name}
                          </NavLink>
                        </li>
                      )
                    ))
                  }
                </ul>
              </li>

              {/* Media Dropdown */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("media")}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  setActiveDropdown(activeDropdown === "media" ? null : "media");
                }}
              >
                <NavLink to="/media" className={getNavLinkClass}>
                  Media
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  to="/contact"
                  className={getNavLinkClass}
                  onClick={() => {
                    setActiveDropdown(null);
                    scrollToTop();
                  }}
                >
                  Contact
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/donation"
                  className={getNavLinkClass}
                  onClick={() => {
                    setActiveDropdown(null);
                  }}
                >
                  Partner With Us
                </NavLink>
              </li>
            </ul>

            {/* Desktop CTA Button */}
            <div className="cta-button">
              <NavLink
                to="/get-started"
                className={({ isActive }) =>
                  isActive ? "cta-primary active" : "cta-primary"
                }
                onClick={() => {
                  setActiveDropdown(null);
                }}
              >
                Register
              </NavLink>
              {/* <NavLink
                to="/free-demo"
                className={({ isActive }) =>
                  isActive ? "cta-secondary active" : "cta-secondary"
                }
                onClick={() => {
                  setActiveDropdown(null);
                }}
              >
                Book Free Demo
              </NavLink> */}
              <ButtonBase
                onClick={() => handleDisplay(display)}
                sx={{
                  padding: '.2rem',
                  backgroundColor: "#ffffff00",
                  color: "#000000ff",
                  borderRadius: ".3rem",
                  ml: 1
                }}
                aria-label="User menu"
              >
                {display === "none" ? (
                  <Stack direction="row" spacing={2}>
                    <Avatar sx={{ width: 35, height: 35 }}>
                      <AccountCircleIcon sx={{fontSize: '4rem'}}/>
                    </Avatar>
                  </Stack>
                ) : (
                  <CloseIcon />
                )}
              </ButtonBase>
              <Box
                sx={{
                  position: "fixed",
                  display: `${display}`,
                  height: "100vh",
                  width: { xs: "100%", sm: "350px" },
                  top: '50px',
                  right: 0,
                  zIndex: 3000,
                  backgroundColor: "#32a059",
                  padding: '1rem',
                  boxShadow: '-2px 0 10px rgba(0,0,0,0.3)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <Box marginBottom={'1rem'} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" sx={{ color: "white", fontWeight: "bold" }}>
                    Menu
                  </Typography>
                  <ButtonBase 
                    onClick={() => handleDisplay(display)}
                    sx={{
                      padding: 1,
                      backgroundColor: "#fff",
                      color: "#000000ff",
                      borderRadius: ".3rem",
                    }}
                    aria-label="Close menu"
                  >
                    <CloseIcon />
                  </ButtonBase>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '1rem', 
                  width: '100%',
                  mt: 2
                }}>
                  {isAuthenticated ? (
                    <>
                      <ButtonBase
                        onClick={() => {
                          alert("Coming Soon !!!");
                          handleDisplay(display);
                        }}
                        sx={{
                          padding: '0.75rem 1rem',
                          backgroundColor: "#fff",
                          color: "#000000ff",
                          borderRadius: ".3rem",
                          textAlign: 'left',
                          fontWeight: '500',
                          justifyContent: 'flex-start'
                        }}
                      >
                        <AccountCircleIcon sx={{ mr: 1 }} />
                        Student Portal
                      </ButtonBase>
                      <ButtonBase
                        onClick={handleLogout}
                        sx={{
                          padding: '0.75rem 1rem',
                          backgroundColor: "#FF6B6B",
                          color: "white",
                          borderRadius: ".3rem",
                          fontWeight: '500',
                          '&:hover': { backgroundColor: "#FF5252" }
                        }}
                      >
                        Logout
                      </ButtonBase>
                    </>
                  ) : (
                    <>
                      <NavLink
                        to="#"
                        className={({ isActive }) =>
                          isActive ? "cta-primary active" : "cta-primary"
                        }
                        onClick={() => {handleDisplay(display); alert("Coming Soon");}}
                        style={{ textAlign: 'center' }}
                      >
                        Student Portal
                      </NavLink>
                    </>
                  )}
                </Box>
              </Box>
            </div>
          </nav>
        </div>

        {/* Mobile Menu Overlay */}
        <div 
          className={`mobile-menu-overlay ${mobileMenuOpen ? "open" : ""}`} 
          onClick={closeAllMenus}
        ></div>

        {/* Mobile Sidebar Navigation */}
        <nav className={`mobile-nav ${mobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-nav-header">
            <div className="logo">
              <NavLink to="/" onClick={() => { scrollToTop(); closeAllMenus(); }}>
                <img src={logo} alt="Company Logo" />
              </NavLink>
            </div>
            <button
              className="mobile-menu-close"
              onClick={closeAllMenus}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
          
          <ul className="mobile-nav-list">
            <li className="mobile-nav-item">
              <NavLink 
                to="/" 
                onClick={() => { scrollToTop(); closeAllMenus(); }} 
                className={getMobileNavLinkClass} 
                end
              >
                Home
              </NavLink>
            </li>
            
            {/* About Mobile */}
            <li className="mobile-nav-item">
              <NavLink 
                to="/about" 
                onClick={closeAllMenus} 
                className={getMobileNavLinkClass}
              >
                Who We Are
              </NavLink>
            </li>
            
            {/* Modules Dropdown - mobile version */}
            <li className="mobile-nav-item dropdown">
              <div 
                className="mobile-nav-link dropdown-trigger"
                onClick={() => toggleMobileDropdown('modules-dropdown-mobile')}
              >
                <span>Modules</span>
                <span className="dropdown-icon">▼</span>
              </div>
              <ul className="dropdown-menu-mobile modules-dropdown-mobile">
                <li>
                  <NavLink
                    to={"/modules"}
                    className={getMobileNavLinkClass}
                    onClick={closeAllMenus}
                    style={{ textTransform: "capitalize" }}
                  >
                    All Modules
                  </NavLink>
                  {modules.map((client) => (
                    <li key={client.id}>
                      <NavLink
                        to={`./modules/category/${client.categories}/?cat=${client.categories}`}
                        className={getMobileNavLinkClass}
                        onClick={closeAllMenus}
                        style={{ textTransform: "capitalize" }}
                      >
                        {client.title}
                      </NavLink>
                    </li>
                  ))}
                </li>
              </ul>
            </li>
            
            {/* Services Dropdown - mobile version */}
            <li className="mobile-nav-item dropdown">
              <div 
                className="mobile-nav-link dropdown-trigger"
                onClick={() => toggleMobileDropdown('services-dropdown-mobile')}
              >
                <span>Services</span>
                <span className="dropdown-icon">▼</span>
              </div>
              <ul className="dropdown-menu-mobile services-dropdown-mobile">
                <li>
                  <NavLink
                    to={"/services"}
                    className={getMobileNavLinkClass}
                    onClick={closeAllMenus}
                    style={{ textTransform: "capitalize" }}
                  >
                    All Services
                  </NavLink>
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "STEAM" &&(
                      <li key={service.id}>
                        <NavLink
                          to={`/services/${service.department_name}/?code=${service.id}`}
                          className={getMobileNavLinkClass}
                          onClick={closeAllMenus}
                        >
                          {service.department_name}
                        </NavLink>
                      </li>)
                    ))
                  }
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "ICT" &&(
                      <li key={service.id}>
                        <NavLink
                          to={`/services/${service.department_name}/?code=${service.id}`}
                          className={getMobileNavLinkClass}
                          onClick={closeAllMenus}
                        >
                          {service.department_name}
                        </NavLink>
                      </li>)
                    ))
                  }
                  {services.length > 0 &&
                    services.map((service) => (
                      service.department_name === "Engineering" &&(
                      <li key={service.id}>
                        <NavLink
                          to={`/services/${service.department_name}/?code=${service.id}`}
                          className={getMobileNavLinkClass}
                          onClick={closeAllMenus}
                        >
                          {service.department_name}
                        </NavLink>
                      </li>)
                    ))
                  }
                </li>
              </ul>
            </li>
            
            <li className="mobile-nav-item">
              <NavLink 
                to="/media" 
                onClick={closeAllMenus} 
                className={getMobileNavLinkClass}
              >
                Media
              </NavLink>
            </li>
            
            <li className="mobile-nav-item">
              <NavLink 
                to="/contact" 
                onClick={() => { closeAllMenus(); scrollToTop(); }} 
                className={getMobileNavLinkClass}
              >
                Contact
              </NavLink>
            </li>
            
            <li className="mobile-nav-item">
              <NavLink 
                to="/donation" 
                onClick={closeAllMenus} 
                className={getMobileNavLinkClass}
              >
                Partner With Us
              </NavLink>
            </li>
          </ul>
          
          {/* Mobile CTA Buttons */}
          <div className="mobile-cta-buttons">
            {/* <NavLink
              to="/free-demo"
              className="cta-secondary"
              onClick={closeAllMenus}
            >
              Book Free Demo
            </NavLink> */}
            
            {/* User menu for mobile */}
            <div className="mobile-user-menu">
              {isAuthenticated ? (
                <>
                  <button
                    className="mobile-user-btn"
                    onClick={() => {
                      alert("Coming Soon !!!");
                      closeAllMenus();
                    }}
                  >
                    <AccountCircleIcon />
                    <span>Student Portal</span>
                  </button>
                  <button
                    className="mobile-logout-btn"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    to="/get-started"
                    className="mobile-user-btn"
                    onClick={closeAllMenus}
                  >
                    Student Registration
                  </NavLink>
                  <button
                    className="mobile-user-btn"
                    onClick={() => { closeAllMenus(); alert("Coming Soon"); }}
                  >
                    Student Portal
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;